/**
 * One requirement, opened: what it is, where it stands, and who was contacted.
 *
 * ONE COMPONENT FOR BOTH SURFACES. The workspace list and the tenant detail tab
 * render this same drawer with the same props; the only difference between them
 * is `showTenantNavigation`, because a "Mieter öffnen" link on the tenant's own
 * page points at the page you are standing on. A second implementation for the
 * tab would be two places for every later change to the timeline, the contact
 * form and the write-refresh contract — the exact split S7's guards exist to
 * prevent.
 *
 * IT OWNS THE OVERLAY, NOT THE LIST. The drawer holds the communication read,
 * its own loading and error state, and which modal is open on top of it. It
 * does NOT hold filters, paging, or the requirement rows: the page that opened
 * it keeps those and passes one row in. So there is no store and no context —
 * the parent owns `selectedRequirement`, this owns everything downstream of it.
 *
 * EVERY VALUE ON SCREEN IS A FIELD OF THE ROW. The badge is `row.status`, the
 * deadline is `row.effective_due_date`, "zuletzt angefragt" is
 * `row.last_requested_at`. Nothing is computed from the timeline below it — in
 * particular `last_requested_at` is NOT the newest entry in `items`, because the
 * backend counts only requests and reminders and a note must not move it. Two
 * answers to that question, one of them derived here, is how the drawer and the
 * list start disagreeing about the same requirement.
 *
 * THE TIMELINE IS READ WHEN THE DRAWER OPENS, never before. A list of fifty rows
 * that each fetched their own history would be fifty requests for a panel nobody
 * has opened; the mount-time read is the one the S8x-1 aggregate exists to make
 * unnecessary.
 *
 * THE TENANCY IS TEXT, NOT A LINK. `/admin/tenancies/:id` does not exist —
 * tenancies live as cards on the tenant page — so the reference number is
 * printed and the way in is the tenant. `RoomDetailCards` records the same
 * refusal for the same reason. Nor does the global drawer offer a documents
 * link: there is no URL contract that opens the tenant's Dokumente tab, and
 * inventing a `?tab=` would be a promise the other page does not keep. The
 * tenant tab CAN offer it, because it already holds the callback that switches
 * tabs in place.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { ResponsiveModal } from "../../../components/ui/responsive-modal";
import { fetchRequirementCommunications } from "../../../api/adminData/tenancyRequirements";
import { formatDateOnly, formatDateTime } from "../../../utils/datetime";
import RequirementContactModal, {
  CONTACT_CHANNEL_OPTIONS,
  CONTACT_KIND_OPTIONS,
} from "./RequirementContactModal";
import RequirementReminderDialog from "./RequirementReminderDialog";
import {
  requirementStatusLabel,
  requirementStatusTone,
  requirementTypeLabel,
} from "./requirementLabels";

/** The badge tones, the same vocabulary the table uses. */
const TONES = {
  positive:
    "border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300",
  warn: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300",
  bad: "border-rose-200 bg-rose-50 text-rose-800 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-300",
  neutral: "border-border bg-muted text-foreground",
  muted: "border-border bg-transparent text-muted-foreground",
};

/** The statuses for which "nobody has asked yet" is a task rather than trivia.
 *  A fulfilled or excused requirement nobody asked about is simply finished. */
const AWAITING_STATUSES = ["open", "overdue"];

/** Wire code → German, built from the form's own option lists so a label can
 *  never say one thing in the timeline and another in the dropdown. */
const KIND_LABELS = Object.fromEntries(
  CONTACT_KIND_OPTIONS.map((o) => [o.value, o.label])
);
const CHANNEL_LABELS = Object.fromEntries(
  CONTACT_CHANNEL_OPTIONS.map((o) => [o.value, o.label])
);

const SECTION = "border-t border-border pt-3";
const TERM = "text-[12px] text-muted-foreground";
const VALUE = "text-[12.5px] font-medium text-foreground";

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
        TONES[requirementStatusTone(status)] || TONES.neutral
      }`}
    >
      {requirementStatusLabel(status)}
    </span>
  );
}

function Fact({ label, children, testId }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={TERM}>{label}</span>
      <span className={`${VALUE} text-right`} data-testid={testId}>
        {children}
      </span>
    </div>
  );
}

/**
 * The evidence line — the table's rule, restated for one row.
 *
 * A deposit names where its truth lives instead of counting documents: it is
 * fulfilled by the tenancy's own fields and can be complete with no document
 * filed anywhere, so "kein passender Nachweis" beside a badge reading "Erfüllt"
 * would put two contradictory sentences next to each other.
 */
function evidenceText(row) {
  if (row.requirement_type === "deposit") return "Kautionsstatus (systemgeführt)";
  const count = Number(row.matching_document_count) || 0;
  if (count === 0) return "Kein passender Nachweis";
  return count === 1 ? "1 Dokument" : `${count} Dokumente`;
}

/** One recorded contact. */
function TimelineEntry({ item }) {
  const kind = KIND_LABELS[item.kind] || item.kind;
  const channel = item.channel ? CHANNEL_LABELS[item.channel] || item.channel : null;

  return (
    <li
      className="rounded-[8px] border border-border px-3 py-2"
      data-testid={`requirement-communication-${item.id}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <span className="text-[12.5px] font-medium text-foreground">
          {kind}
          {channel ? (
            <span className="font-normal text-muted-foreground">{` · ${channel}`}</span>
          ) : null}
        </span>
        {/* `occurred_at`, not `created_at`: when the contact HAPPENED is the
            fact being recorded — a mail sent yesterday may be written down
            today, and the row's own creation time is bookkeeping. */}
        <span className="text-[11.5px] text-muted-foreground">
          {formatDateTime(item.occurred_at)}
        </span>
      </div>
      <p className="m-0 mt-0.5 text-[11.5px] text-muted-foreground">
        {/* The backend returns a null actor for work no human did and does not
            invent an account to name (migration 176). "System" is this
            surface's word for that null, chosen here rather than stored. */}
        {item.actor_name || "System"}
      </p>
      {item.subject ? (
        <p className="m-0 mt-1 text-[12px] text-foreground">{item.subject}</p>
      ) : null}
      {item.note ? (
        <p className="m-0 mt-1 whitespace-pre-wrap text-[12px] text-muted-foreground">
          {item.note}
        </p>
      ) : null}
    </li>
  );
}

/**
 * @param {{
 *   row: object|null,
 *   onClose: () => void,
 *   onRequirementRefresh?: () => void,
 *   showTenantNavigation?: boolean,
 *   onGoToDocuments?: () => void,
 *   actions?: object,
 * }} props
 */
export default function RequirementDetailDrawer({
  row,
  onClose,
  onRequirementRefresh,
  showTenantNavigation = true,
  onGoToDocuments,
  actions,
}) {
  const [timeline, setTimeline] = useState({ loading: true, error: null, items: [] });
  const [contactOpen, setContactOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);

  // Newest read wins: closing one requirement and opening another must not be
  // repainted by the first one's response landing late.
  const requestSeq = useRef(0);

  const tenancyId = row?.tenancy_id ?? null;
  const requirementType = row?.requirement_type ?? null;

  const loadTimeline = useCallback(() => {
    if (!tenancyId || !requirementType) return Promise.resolve();
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setTimeline((prev) => ({ ...prev, loading: true, error: null }));

    return fetchRequirementCommunications(tenancyId, requirementType)
      .then((res) => {
        if (requestSeq.current !== seq) return;
        // Rendered in the order the backend returned. It orders by
        // `occurred_at` first and breaks ties deterministically; re-sorting
        // here would be a second opinion about which of two contacts in the
        // same minute came first.
        setTimeline({
          loading: false,
          error: null,
          items: Array.isArray(res?.items) ? res.items : [],
        });
      })
      .catch((e) => {
        if (requestSeq.current !== seq) return;
        setTimeline({
          loading: false,
          error: e?.message || "Kontakte konnten nicht geladen werden.",
          items: [],
        });
      });
  }, [tenancyId, requirementType]);

  // Keyed on the identity, so switching rows inside an open drawer refetches
  // and closing it does not.
  useEffect(() => {
    if (!tenancyId || !requirementType) return;
    loadTimeline();
  }, [tenancyId, requirementType, loadTimeline]);

  /**
   * A contact was filed.
   *
   * THE WRITE SUCCEEDED; THE READS ARE SEPARATE. Both refreshes are fired and
   * neither is allowed to turn a stored contact into a reported failure — a
   * timeline that cannot reload shows its own error with a retry, beside a row
   * that is already in the database. Tying the success message to the second
   * request is how a user ends up filing the same call twice.
   */
  const onContactCreated = useCallback(() => {
    setContactOpen(false);
    loadTimeline();
    if (typeof onRequirementRefresh === "function") onRequirementRefresh();
  }, [loadTimeline, onRequirementRefresh]);

  /**
   * A reminder attempt finished — sent, or declined because somebody else
   * already owns today's send.
   *
   * The same two reads as a filed contact, and for the same reason: the
   * server wrote the timeline entry and recomputed `last_requested_at`, so
   * both are re-read rather than guessed at here. The dialog closes itself,
   * which is why this does not.
   */
  const onReminderSent = useCallback(() => {
    loadTimeline();
    if (typeof onRequirementRefresh === "function") onRequirementRefresh();
  }, [loadTimeline, onRequirementRefresh]);

  if (!row) return null;

  // Whether anybody could still be chased about this requirement. The SAME
  // list `awaiting` uses, but without its `last_requested_at` half: a proof
  // that was asked for last week and still has not arrived is exactly the
  // case a second reminder exists for.
  const awaitingAction = AWAITING_STATUSES.includes(row.status);
  const awaiting = !row.last_requested_at && awaitingAction;

  return (
    <>
      <ResponsiveModal
        open
        onOpenChange={(open) => (!open ? onClose() : null)}
        title={
          <span className="flex flex-wrap items-center gap-2">
            {requirementTypeLabel(row.requirement_type)}
            <StatusBadge status={row.status} />
          </span>
        }
        contentClassName="sm:max-w-[560px]"
      >
        <div
          className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto"
          data-testid="requirement-detail-drawer"
        >
          {/* ── Kontext ─────────────────────────────────────────────── */}
          <div className="grid gap-1.5">
            <Fact label="Mieter" testId="requirement-detail-tenant">
              {row.tenant_name || "—"}
            </Fact>
            {/* Text, not a link — there is no tenancy route to point at. */}
            <Fact label="Mietverhältnis" testId="requirement-detail-tenancy">
              {row.tenancy_reference_number || "—"}
            </Fact>
            <Fact label="Objekt / Zimmer" testId="requirement-detail-object">
              {[row.unit_short_id || row.unit_title, row.room_name]
                .filter(Boolean)
                .join(" · ") || "—"}
            </Fact>
            {row.requirement_type === "liability_insurance" &&
            row.evidence_tenant_name ? (
              <Fact label="Haftpflichtnachweis von">
                {row.evidence_tenant_name}
              </Fact>
            ) : null}
          </div>

          {/* ── Status ──────────────────────────────────────────────── */}
          <div className={`${SECTION} grid gap-1.5`}>
            <Fact label="Frist" testId="requirement-detail-due">
              {row.effective_due_date
                ? formatDateOnly(row.effective_due_date)
                : "Keine Frist"}
            </Fact>
            {row.due_date_override ? (
              <Fact label="Individuelle Frist">
                {formatDateOnly(row.due_date_override)}
              </Fact>
            ) : null}
            <Fact label="Nachweis" testId="requirement-detail-evidence">
              {evidenceText(row)}
            </Fact>
            {row.waived_at ? (
              <Fact label="Erlassen" testId="requirement-detail-waived">
                {row.waive_reason || formatDateOnly(row.waived_at)}
              </Fact>
            ) : null}
          </div>

          {/* ── Nachfassen ──────────────────────────────────────────── */}
          <div className={`${SECTION} grid gap-2`}>
            <Fact label="Zuletzt angefragt" testId="requirement-detail-last-requested">
              {row.last_requested_at ? (
                formatDateTime(row.last_requested_at)
              ) : awaiting ? (
                <span className="text-muted-foreground">Noch nicht angefragt</span>
              ) : (
                "—"
              )}
            </Fact>
            <div className="flex flex-wrap gap-2">
              {/* Only for a requirement somebody could still be chased
                  about. `AWAITING_STATUSES` is the drawer's existing answer
                  to that question and happens to be exactly the set the
                  orchestrator will send for; a second list here would be two
                  places to keep in step. Whether a DEPOSIT in that state is
                  really the tenant's to act on is not decided here — that is
                  `reason_code`, it is engine vocabulary, and the server
                  answers it with `not_sendable`. */}
              {awaitingAction ? (
                <button
                  type="button"
                  onClick={() => setReminderOpen(true)}
                  data-testid="requirement-detail-send-reminder"
                  className="rounded-[7px] bg-primary px-[10px] py-[5px] text-[12.5px] font-medium text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  Erinnerung jetzt senden
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => setContactOpen(true)}
                data-testid="requirement-detail-contact"
                className="rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                Kontakt dokumentieren
              </button>
              {showTenantNavigation && row.tenant_id ? (
                <Link
                  to={`/admin/tenants/${encodeURIComponent(row.tenant_id)}`}
                  data-testid="requirement-detail-tenant-link"
                  className="inline-flex items-center rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  Mieter öffnen
                </Link>
              ) : null}
              {/* Only where a callback exists to switch tabs in place. */}
              {typeof onGoToDocuments === "function" ? (
                <button
                  type="button"
                  onClick={onGoToDocuments}
                  data-testid="requirement-detail-documents"
                  className="rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  Dokumente anzeigen
                </button>
              ) : null}
            </div>
          </div>

          {/* ── Verlauf ─────────────────────────────────────────────── */}
          <div className={`${SECTION} flex min-h-0 flex-col gap-2`}>
            <h3 className="m-0 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground">
              Verlauf
            </h3>

            {timeline.loading ? (
              <p
                className="m-0 text-[12.5px] text-muted-foreground"
                data-testid="requirement-communications-loading"
              >
                Kontakte werden geladen …
              </p>
            ) : timeline.error ? (
              // Panel-local, never a page-level error: the requirement itself
              // loaded fine and the rest of the drawer is still true.
              <div
                role="alert"
                data-testid="requirement-communications-error"
                className="rounded-[8px] border border-destructive/30 bg-destructive/5 px-3 py-2"
              >
                <p className="m-0 text-[12.5px] text-destructive">{timeline.error}</p>
                <button
                  type="button"
                  onClick={loadTimeline}
                  className="mt-2 rounded-[7px] border border-border bg-card px-[10px] py-[4px] text-[12px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  Erneut versuchen
                </button>
              </div>
            ) : timeline.items.length === 0 ? (
              <p
                className="m-0 text-[12.5px] text-muted-foreground"
                data-testid="requirement-communications-empty"
              >
                Noch keine Kontakte dokumentiert.
              </p>
            ) : (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">
                {timeline.items.map((item) => (
                  <TimelineEntry key={item.id} item={item} />
                ))}
              </ul>
            )}
          </div>

          {/* ── Aktionen ────────────────────────────────────────────── */}
          {actions ? (
            <div className={`${SECTION} flex flex-wrap gap-2`}>
              <button
                type="button"
                onClick={() => actions.onEditDueDate(row)}
                className="rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                Frist ändern
              </button>
              {row.waived_at ? (
                <>
                  <button
                    type="button"
                    onClick={() => actions.onEditReason(row)}
                    className="rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    Begründung bearbeiten
                  </button>
                  <button
                    type="button"
                    onClick={() => actions.onUnwaive(row)}
                    className="rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    Erlass aufheben
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => actions.onWaive(row)}
                  className="rounded-[7px] border border-border bg-card px-[10px] py-[5px] text-[12.5px] font-medium text-rose-700 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 dark:text-rose-300"
                >
                  Pflicht erlassen
                </button>
              )}
            </div>
          ) : null}
        </div>
      </ResponsiveModal>

      {contactOpen ? (
        <RequirementContactModal
          row={row}
          onClose={() => setContactOpen(false)}
          onCreated={onContactCreated}
        />
      ) : null}

      {reminderOpen ? (
        <RequirementReminderDialog
          row={row}
          onClose={() => setReminderOpen(false)}
          onSent={onReminderSent}
        />
      ) : null}
    </>
  );
}
